import { css } from "@emotion/react";
import { useEffect, useMemo } from "react";
import { ShimmerEffect } from "../ShimmerEffect";
import { isNonNullish } from "../@utils/isNonNullish";
import { useFieldGroup } from "../FieldGroup";
import { MediaFallback, MediaStyleProps } from "./MediaFallback";

export interface VideoThumbnailProps
  extends React.ComponentPropsWithoutRef<"video">, MediaStyleProps {
  videoRef?: React.RefObject<HTMLVideoElement>;
  noVideo?: boolean;
  /**
   * srcでURLを指定する代わりに、Fileオブジェクトを指定することができます。
   */
  file?: File;
}

export const VideoThumbnail = (props: VideoThumbnailProps) => {
  const {
    videoRef,
    aspectRatio,
    width,
    height,
    children,
    src: propsSrc,
    file,
    noVideo,
    style,
    className,
    ...rest
  } = props;

  const { loading: fieldLoading } = useFieldGroup({});

  const objectUrl = useMemo(() => {
    return file ? URL.createObjectURL(file) : undefined;
  }, [file]);

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const src = objectUrl ?? propsSrc;

  const noSource = !isNonNullish(children) && src === undefined;

  if (noSource || fieldLoading) {
    const fallback = (
      <MediaFallback
        style={style}
        className={className}
        width={width}
        height={height}
        aspectRatio={aspectRatio}
      >
        No Video
      </MediaFallback>
    );
    if (noVideo) {
      return fallback;
    } else {
      return <ShimmerEffect active>{fallback}</ShimmerEffect>;
    }
  }

  return (
    <video
      {...rest}
      src={src}
      width={width}
      height={height}
      style={style}
      ref={videoRef}
      className={className}
      css={css({
        display: "flex",
        objectFit: "cover",
        // @see https://github.com/emotion-js/emotion/issues/3011
        aspectRatio: aspectRatio !== undefined ? `${aspectRatio}` : undefined,
        width: width ?? "100%",
        height: height ?? "auto",
      })}
    >
      {children}
    </video>
  );
};
